import Api from "@/_helpers/Api"
import { trans } from "@/_providers/Translation"
import Pagination from "@/components/Pagination"
import DateRange from "@/components/partials/DateRange"
import Dropdown from "@/components/partials/Dropdown"
import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import { useRef } from "react"
import Skeleton from "react-loading-skeleton"
import { useHistory, useLocation } from "react-router-dom"

const FILTER_KEYS = [
  "employee_id",
  "category_id",
  "section_id",
  "date_from",
  "date_to",
]

export default function ActivityLog() {
  const history = useHistory()
  const location = useLocation()

  const employeesDropdownRef = useRef(null)
  const debounceRef = useRef(null)
  const pendingRef = useRef({})

  // The URL is the single source of truth for the filters and the page.
  // Deriving them from `location.search` means a page refresh transparently
  // restores the filters and sends them with the request.
  const searchParams = new URLSearchParams(location.search)

  const filters = FILTER_KEYS.reduce((acc, key) => {
    acc[key] = searchParams.get(key) || ""
    return acc
  }, {})

  const page = Number(searchParams.get("page")) || 1

  // The query key includes the filters and page, so any URL param change
  // automatically triggers a new request with those params appended.
  //
  // No `initialData`/`keepPreviousData`: that way `isLoading` is true only the
  // first time a given set of params is requested (nothing cached yet). When
  // the same params were requested before, React Query serves the cached data
  // instantly (`isLoading` stays false) and refetches in the background,
  // swapping in the fresh data once the request finishes.
  const { data, isLoading } = useQuery({
    queryKey: ["activities/all", filters, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        group: "all",
        page,
        ...filters,
      })

      const res = await Api.get(`activities/all?${params.toString()}`)
      return res.data || {}
    },
  })

  function pushParams(changes, { resetPage = true } = {}) {
    const params = new URLSearchParams(location.search)

    Object.entries(changes).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    })

    // Any filter change resets pagination back to the first page.
    if (resetPage) {
      params.delete("page")
    }

    history.push("?" + params.toString())
  }

  function handleFilterChange(e, interval = 300) {
    const { name, value } = e.target

    // Merge changes so several rapid updates (e.g. a date range firing both
    // start and end) are applied to the URL together instead of overwriting.
    pendingRef.current[name] = value

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const changes = pendingRef.current
      pendingRef.current = {}
      pushParams(changes)
    }, interval)
  }

  function handleClearFilters() {
    clearTimeout(debounceRef.current)
    pendingRef.current = {}

    const params = new URLSearchParams(location.search)
    FILTER_KEYS.forEach((key) => params.delete(key))
    params.delete("page")
    history.push("?" + params.toString())

    employeesDropdownRef.current.reset()
  }

  const handlePage = (selectedPage) => {
    pushParams({ page: selectedPage.selected + 1 }, { resetPage: false })
  }

  const handleShowActivity = (id) => {
    history.replace(`${location.search}&show_activity=${id}`)
  }

  return (
    <div className="pannel">
      <div className="settings-activity-log">
        <h3>{trans("tabs.activityLog")}</h3>

        <form className="filter-standard schedule-filter">
          <div className="col">
            <Dropdown
              url="partners/employees/all?active=1&sort=name"
              renderText={(data) => {
                return `${data.name} ${data.lastname}`
              }}
              onChange={(data) => {
                handleFilterChange({
                  target: {
                    name: "employee_id",
                    value: data.id,
                  },
                })
              }}
              inputIdName="employee_id"
              inputIdValue={filters.employee_id}
              inputPlaceholder={trans("labels.user")}
              ref={employeesDropdownRef}
            />
          </div>
          <div className="col">
            <select
              name="category_id"
              value={filters.category_id}
              onChange={handleFilterChange}
            >
              <option value="">{trans("random.allCategories")}</option>
              {data?.filters?.categories?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.translation}
                </option>
              ))}
            </select>
          </div>
          <div className="col">
            <select
              name="section_id"
              value={filters.section_id}
              onChange={handleFilterChange}
            >
              <option value="">{trans("random.allSections")}</option>
              {data?.filters?.sections?.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.translation}
                </option>
              ))}
            </select>
          </div>
          <div className="col">
            <DateRange
              startDate={filters.date_from}
              endDate={filters.date_to}
              onStartDateChange={(date) => {
                handleFilterChange({
                  target: { name: "date_from", value: date },
                })
              }}
              onEndDateChange={(date) => {
                handleFilterChange({ target: { name: "date_to", value: date } })
              }}
            />
          </div>
          <div className="col">
            <button
              type="button"
              className="button-clear-filter"
              onClick={handleClearFilters}
            >
              {trans("buttons.clearFilter")}
            </button>
          </div>
        </form>
        <hr />
        {isLoading ? (
          <Skeleton count={10} height={45} />
        ) : (
          <>
            <div className="table-scroller">
              <table>
                <thead>
                  <tr>
                    <th>Име</th>
                    <th>Дейност</th>
                    <th>Статус</th>
                    <th>Потребител</th>
                    <th>Дата</th>
                    <th>IP адрес</th>
                    <th>Приоритет</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((activity) => {
                    const before = activity.before || {}
                    const after = activity.after || {}
                    const changes = Object.keys(after)
                      .filter(
                        (key) =>
                          key !== "updated_at" && after[key] !== before[key],
                      )
                      .map((key) => ({
                        field: key,
                        from: before[key],
                        to: after[key],
                      }))
                    const isUpdated = activity.event === "updated"

                    return (
                      <tr
                        key={activity.id}
                        onClick={
                          !isUpdated || changes.length === 1
                            ? () => handleShowActivity(activity.id)
                            : undefined
                        }
                        style={{
                          cursor:
                            !isUpdated || changes.length === 1
                              ? "pointer"
                              : "default",
                        }}
                      >
                        <td>{activity.title || activity.trans || "-"}</td>
                        <td>
                          {isUpdated && changes.length === 1 && (
                            <div className="activity-location">
                              <div className="activity-location-side">
                                <span className="activity-location-label">
                                  {trans("tables." + changes[0].field ?? "-")}
                                </span>
                                <span className="activity-location-value">
                                  {moment(changes[0].from).isValid() ? moment(changes[0].from).format("DD.MM.YYYY HH:mm") : changes[0].from ?? "-"}
                                </span>
                              </div>
                              <span className="activity-location-arrow">→</span>
                              <div className="activity-location-side">
                                <span className="activity-location-label">
                                  {trans("tables." + changes[0].field ?? "-")}
                                </span>
                                <span className="activity-location-value">
                                  {moment(changes[0].to).isValid() ? moment(changes[0].to).format("DD.MM.YYYY HH:mm") : changes[0].to ?? "-"}
                                </span>
                              </div>
                            </div>
                          )}
                          {isUpdated && changes.length > 1 && (
                            <span
                              className="link"
                              onClick={() => handleShowActivity(activity.id)}
                            >
                              {trans("buttons.showAllChanges")}
                            </span>
                          )}
                        </td>
                        <td>{activity.status || "-"}</td>
                        <td>{activity.causer?.full_name || "-"}</td>
                        <td>
                          {`${moment(activity.created_at).format("DD.MM.YYYY")} / ${moment(activity.created_at).format("HH:mm")}`}
                        </td>
                        <td>{activity.ip_address || "-"}</td>
                        <td>{activity.priority || "-"}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <Pagination pages={data?.pages} page={page} handlePage={handlePage} />
      </div>
    </div>
  )
}
